import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { UI } from "@wwimmo/ui";
import styles from "./Attendance.module.css";
import { useParams } from "react-router";
import { AttendanceList } from "./AttendanceList";
import { useHistory } from "react-router-dom";
import { AssemblyUnlockModal } from "src/screens/assemblies/assembly/agenda/modal/AssemblyUnlockModal";

interface Params {
    assemblyid?: string;
}

interface IAttendanceProps {
    isLiveModeSmallView: boolean;
}

const AttendanceMobileDropdownBase = (props: IAttendanceProps) => {
    const { assemblyid } = useParams<Params>();
    const { assemblyStore, uiStore, attendanceListStore } = useContext(RootStoreContext);

    const onSelectDropdown = useCallback(
        (realestatId: string) => {
            if (realestatId !== attendanceListStore.selectedRealestateId) {
                attendanceListStore.setSelectedRealestateId(realestatId);
                if (assemblyid && realestatId) {
                    attendanceListStore.loadAttendanceByRealestateId(
                        assemblyid,
                        attendanceListStore.selectedRealestateId
                    );
                }
            }
        },
        [attendanceListStore, assemblyid]
    );

    return uiStore.isMobile || props.isLiveModeSmallView ? (
        <UI.Dropdown
            id="sort"
            onSelect={onSelectDropdown}
            items={
                assemblyStore.currentAssembly
                    ? assemblyStore.currentAssembly.assemblyrealestates.map((item) => ({
                          label: item.realestate.name,
                          value: item.realestate.id
                      }))
                    : [{ label: "", value: "" }]
            }
            label={
                attendanceListStore.selectedCurrentAssemblyRealestate
                    ? attendanceListStore.selectedCurrentAssemblyRealestate.realestate.name
                    : assemblyStore.currentAssembly?.assemblyrealestates[0]?.realestate.name || ""
            }
            className={styles.RealestateDropdown}
        />
    ) : (
        <React.Fragment />
    );
};
const AttendanceMobileDropdown = observer(AttendanceMobileDropdownBase);

const AttendanceBase = (props: IAttendanceProps) => {
    const { t } = useTranslation();
    const { assemblyid } = useParams<Params>();
    const { navStore, assemblyStore, authStore, attendanceListStore, agendaitemStore } = useContext(RootStoreContext);
    const history = useHistory();
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const onClickUnlockAssembly = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    useEffect(() => {
        if (
            assemblyStore.currentAssembly === undefined ||
            assemblyStore.currentAssemblyRealestates.length !== assemblyStore.currentAssembly.assemblyrealestates.length
        ) {
            assemblyStore.loadCurrentAssembly(assemblyid || "");
        }

        if (assemblyid) {
            attendanceListStore.checkAndActualizeAssemblyRealestateTenants(assemblyid || "", true);
        }
        return () => {
            attendanceListStore.fullReset();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly?.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly?.date || ""
                )}` || ""
            );
        }

        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [authStore.user?.role, assemblyStore.currentAssembly, navStore, t]);

    useEffect(() => {
        if (assemblyStore.currentAssembly?.livemode) {
            history.push(
                selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
        }
    }, [assemblyStore.currentAssembly?.livemode, history, authStore.user?.role, assemblyStore.currentAssembly?.id]);

    const onResetAttendanceList = useCallback(
        (e: any) => {
            e.preventDefault();
            attendanceListStore.resetAttendanceList();

            attendanceListStore.setSelectedRealestateId(
                assemblyStore.currentAssembly?.assemblyrealestates[0]?.realestate.id ?? ""
            );
        },
        [attendanceListStore, assemblyStore.currentAssembly?.assemblyrealestates]
    );

    const onSaveAttendanceList = useCallback(
        (e: any) => {
            e.preventDefault();
            attendanceListStore.saveAttendanceList(assemblyid || "");
        },
        [attendanceListStore, assemblyid]
    );

    const onDownloadAttendanceList = useCallback(
        (e: any) => {
            e.preventDefault();
            attendanceListStore.generateDownloadAttendanceList(assemblyid || "");
        },
        [attendanceListStore, assemblyid]
    );

    const showAttendanceList =
        assemblyStore.currentAssembly !== undefined && assemblyStore.currentAssembly.assemblyrealestates?.length > 0;

    return (
        <div className={assemblyStore.currentAssembly?.livemode ? undefined : styles.Container}>
            {isReadOnly ? (
                <>
                    <div className={styles.BlockedBanner}>
                        <div className={styles.BannerLeft}>
                            <UI.Icon className={styles.LockIcon} icon={UI.SVGIcon.Lock} size={"small"} />
                            <div className={styles.BannerText}>{t("screens.assemblies.agenda.locked_info")}</div>
                        </div>
                        <div className={styles.BannerLink} onClick={onClickUnlockAssembly}>
                            {t("screens.assemblies.agenda.unlock").toUpperCase()}
                        </div>
                    </div>
                    <AssemblyUnlockModal />
                </>
            ) : null}
            <UI.Card title={t("screens.assemblies.attendanceList").toString()}>
                <AttendanceMobileDropdown isLiveModeSmallView={props.isLiveModeSmallView} />
                {showAttendanceList ? (
                    <AttendanceList assemblyid={assemblyid || ""} isLiveModeSmallView={props.isLiveModeSmallView} />
                ) : (
                    <React.Fragment />
                )}
            </UI.Card>
            <div className={styles.ButtonRow}>
                <UI.Button
                    disabled={!attendanceListStore.hasCurrentAssemblyAttendancesChanged || isReadOnly}
                    onClick={onSaveAttendanceList}
                    label={t("labels.save")}
                    id="assembly-submit-button"
                    className={styles.SubmitButton}
                />
                <UI.Button
                    label={t("labels.discard")}
                    disabled={!attendanceListStore.hasCurrentAssemblyAttendancesChanged || isReadOnly}
                    onClick={onResetAttendanceList}
                    className={styles.DiscardButton}
                    variant="outline-primary"
                />
                {attendanceListStore.isAttendancePDFDownloading ? (
                    <UI.RotatingSpinner noLogo={true} size={25} className="d-flex justify-content-center" />
                ) : (
                    <UI.Button
                        label={t("screens.assemblies.attendance.download")}
                        disabled={false}
                        onClick={onDownloadAttendanceList}
                        className={styles.DownloadButton}
                        variant="outline-primary"
                    />
                )}
            </div>
        </div>
    );
};

export const Attendance = Sentry.withProfiler(observer(AttendanceBase));
